import { useCallback, useEffect, useState } from 'react';
import { managementRequest } from '../services/nuaTalkerNtvaFlow.service';
import { parseOfferBagName } from '../utils/offerBags';

/**
 * Determina si el cliente logueado tiene una "oferta" pendiente de reclamar.
 * Una oferta es simplemente una bolsa de consumo (ver src/utils/offerBags.js)
 * con bagName en formato `oferta|titulo|descripcion` y `active:false`. La crea
 * un admin a mano (o clonando una plantilla) desde el panel de Accounts —
 * no hay config en código ni backend nuevo.
 *
 * Siempre consulta las bolsas frescas contra el backend (sin caché).
 */
const useClaimOffers = (client) => {
  const clientId = client?.id;
  const [pendingOffer, setPendingOffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const checkOffers = useCallback(async () => {
    if (!clientId) {
      setPendingOffer(null);
      return;
    }

    setLoading(true);
    try {
      const bags = await managementRequest('GET', `/client_limit/${clientId}`);
      const pending = (Array.isArray(bags) ? bags : [])
        .filter((bag) => bag.active === false)
        .map((bag) => ({ bag, ...parseOfferBagName(bag.bagName) }))
        .find((o) => o.isOffer);

      setPendingOffer(pending || null);
    } catch (err) {
      console.error('[useClaimOffers] Error consultando bolsas:', err);
      setPendingOffer(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    checkOffers();
  }, [checkOffers]);

  /** Activa la MISMA bolsa (no crea una nueva): active:false -> true. */
  const claim = async () => {
    if (!pendingOffer?.bag || !clientId) return false;
    setClaiming(true);
    try {
      await managementRequest('PUT', '/client_limit', {
        ...pendingOffer.bag,
        active: true,
        clientId,
      });
      setPendingOffer(null);
      return true;
    } catch (err) {
      console.error('[useClaimOffers] Error reclamando oferta:', err);
      throw err;
    } finally {
      setClaiming(false);
    }
  };

  return { pendingOffer, loading, claiming, claim, dismiss: () => setPendingOffer(null) };
};

export default useClaimOffers;
